import { useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * AutoTranslator — Automatically translates ALL visible text on the page.
 *
 * How it works:
 * 1. Walks the entire DOM tree and collects text nodes
 * 2. Batches unique strings and sends them to /api/translate
 * 3. Replaces text nodes with translated versions
 * 4. Uses MutationObserver to catch dynamically added content
 * 5. Caches translations in localStorage to avoid repeat API calls
 *
 * Place this component once inside your Router (needs useLocation).
 */

const CACHE_PREFIX = 'autotranslate_v2_';
const CACHE_TTL = 14 * 24 * 60 * 60 * 1000; // 14 days

// Elements whose text content should NOT be translated
const SKIP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'KBD', 'SAMP',
  'TEXTAREA', 'INPUT', 'SELECT', 'OPTION', 'SVG', 'PATH', 'CIRCLE',
  'RECT', 'LINE', 'POLYLINE', 'POLYGON', 'ELLIPSE', 'G', 'DEFS',
  'CLIPPATH', 'MASK', 'PATTERN', 'IMAGE', 'USE', 'SYMBOL',
]);

// Attributes to translate
const TRANSLATABLE_ATTRS = ['placeholder', 'title', 'aria-label', 'alt'];

// Minimum text length to bother translating
const MIN_TEXT_LENGTH = 2;

// Batch size for API calls
const BATCH_SIZE = 40;

// Debounce delay for mutation observer
const DEBOUNCE_MS = 300;

interface CacheEntry {
  t: string;   // translated text
  ts: number;  // timestamp
}

function getCacheKey(lang: string): string {
  return `${CACHE_PREFIX}${lang}`;
}

function loadCache(lang: string): Record<string, CacheEntry> {
  try {
    const raw = localStorage.getItem(getCacheKey(lang));
    if (raw) return JSON.parse(raw);
  } catch {
    localStorage.removeItem(getCacheKey(lang));
  }
  return {};
}

function saveCache(lang: string, cache: Record<string, CacheEntry>): void {
  try {
    // Prune expired entries if cache is large
    const now = Date.now();
    if (Object.keys(cache).length > 1000) {
      for (const key of Object.keys(cache)) {
        if (now - cache[key].ts > CACHE_TTL) delete cache[key];
      }
    }
    localStorage.setItem(getCacheKey(lang), JSON.stringify(cache));
  } catch {
    // localStorage full — clear old translation caches
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k?.startsWith(CACHE_PREFIX)) localStorage.removeItem(k);
    }
  }
}

/**
 * Check if a string is "translatable" (not just numbers, symbols, etc.)
 */
function isTranslatable(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length < MIN_TEXT_LENGTH) return false;
  // Skip if it's only numbers, punctuation, whitespace, or special chars
  if (/^[\d\s\W]+$/.test(trimmed) && !/[a-zA-Z\u00C0-\u024F\u0400-\u04FF]/.test(trimmed)) return false;
  // Skip URLs
  if (/^https?:\/\//.test(trimmed)) return false;
  // Skip email addresses
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return false;
  return true;
}

/**
 * Collect all text nodes and translatable attributes from a root element
 */
function collectTranslatableContent(root: Element): {
  textNodes: { node: Text; original: string }[];
  attrNodes: { element: Element; attr: string; original: string }[];
} {
  const textNodes: { node: Text; original: string }[] = [];
  const attrNodes: { element: Element; attr: string; original: string }[] = [];

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode(node) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        if (SKIP_TAGS.has(el.tagName)) return NodeFilter.FILTER_REJECT;
        // Skip already-translated elements
        if (el.hasAttribute('data-translated')) return NodeFilter.FILTER_REJECT;
        // Skip hidden elements
        if (el.hasAttribute('hidden')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
      if (node.nodeType === Node.TEXT_NODE) {
        return NodeFilter.FILTER_ACCEPT;
      }
      return NodeFilter.FILTER_SKIP;
    },
  });

  let current = walker.nextNode();
  while (current) {
    if (current.nodeType === Node.TEXT_NODE) {
      const text = current.textContent || '';
      if (isTranslatable(text)) {
        textNodes.push({ node: current as Text, original: text.trim() });
      }
    } else if (current.nodeType === Node.ELEMENT_NODE) {
      const el = current as Element;
      for (const attr of TRANSLATABLE_ATTRS) {
        const val = el.getAttribute(attr);
        if (val && isTranslatable(val)) {
          attrNodes.push({ element: el, attr, original: val.trim() });
        }
      }
    }
    current = walker.nextNode();
  }

  return { textNodes, attrNodes };
}

const AutoTranslator: React.FC = () => {
  const { language } = useLanguage();
  const observerRef = useRef<MutationObserver | null>(null);
  const translatingRef = useRef(false);
  const pendingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track which original->translated pairs we've already applied (avoid re-processing)
  const appliedRef = useRef<Set<string>>(new Set());
  const lastLangRef = useRef(language);

  /**
   * Translate the entire page
   */
  const translatePage = useCallback(async () => {
    if (language === 'en' || translatingRef.current) {
      if (translatingRef.current) pendingRef.current = true;
      return;
    }

    translatingRef.current = true;

    try {
      const root = document.body;
      if (!root) return;

      const { textNodes, attrNodes } = collectTranslatableContent(root);

      // Collect unique strings to translate
      const cache = loadCache(language);
      const uniqueTexts = new Map<string, boolean>(); // text -> needsTranslation

      for (const { original } of textNodes) {
        if (!appliedRef.current.has(original) && !uniqueTexts.has(original)) {
          uniqueTexts.set(original, !(original in cache && Date.now() - cache[original].ts < CACHE_TTL));
        }
      }
      for (const { original } of attrNodes) {
        if (!uniqueTexts.has(original)) {
          uniqueTexts.set(original, !(original in cache && Date.now() - cache[original].ts < CACHE_TTL));
        }
      }

      // Split into cached and uncached
      const uncached = Array.from(uniqueTexts.entries())
        .filter(([, needs]) => needs)
        .map(([text]) => text);

      // Translate uncached strings in batches
      if (uncached.length > 0) {
        for (let i = 0; i < uncached.length; i += BATCH_SIZE) {
          const batch = uncached.slice(i, i + BATCH_SIZE);
          try {
            const API_BASE = process.env.REACT_APP_API_URL || '/api';
            const response = await fetch(`${API_BASE}/translate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                texts: batch,
                source: 'en',
                target: language,
              }),
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.translations) {
                const now = Date.now();
                for (let j = 0; j < batch.length; j++) {
                  if (data.translations[j] && data.translations[j] !== batch[j]) {
                    cache[batch[j]] = { t: data.translations[j], ts: now };
                  }
                }
                saveCache(language, cache);
              }
            }
          } catch (err) {
            console.warn('[AutoTranslator] Batch translation failed:', err);
          }
        }
      }

      // Apply translations to text nodes
      for (const { node, original } of textNodes) {
        const entry = cache[original];
        if (entry && node.textContent) {
          // Preserve leading/trailing whitespace from original
          const leading = node.textContent.match(/^\s*/)?.[0] || '';
          const trailing = node.textContent.match(/\s*$/)?.[0] || '';
          node.textContent = leading + entry.t + trailing;
          // Mark parent as translated
          if (node.parentElement) {
            node.parentElement.setAttribute('data-translated', 'true');
            node.parentElement.setAttribute('data-original', original);
          }
          appliedRef.current.add(original);
        }
      }

      // Apply translations to attributes
      for (const { element, attr, original } of attrNodes) {
        const entry = cache[original];
        if (entry) {
          element.setAttribute(attr, entry.t);
          element.setAttribute(`data-original-${attr}`, original);
        }
      }
    } catch (err) {
      console.warn('[AutoTranslator] Translation error:', err);
    } finally {
      translatingRef.current = false;

      // If new content was added while we were translating, translate again
      if (pendingRef.current) {
        pendingRef.current = false;
        setTimeout(translatePage, 200);
      }
    }
  }, [language]);

  /**
   * Restore original English text (when switching back to English or changing language)
   */
  const restoreOriginals = useCallback(() => {
    // Restore text nodes
    const translated = document.querySelectorAll('[data-translated]');
    translated.forEach((el) => {
      const original = el.getAttribute('data-original');
      if (original && el.textContent) {
        const leading = el.textContent.match(/^\s*/)?.[0] || '';
        const trailing = el.textContent.match(/\s*$/)?.[0] || '';
        el.textContent = leading + original + trailing;
      }
      el.removeAttribute('data-translated');
      el.removeAttribute('data-original');
    });

    // Restore attributes
    for (const attr of TRANSLATABLE_ATTRS) {
      const els = document.querySelectorAll(`[data-original-${attr}]`);
      els.forEach((el) => {
        const original = el.getAttribute(`data-original-${attr}`);
        if (original) {
          el.setAttribute(attr, original);
          el.removeAttribute(`data-original-${attr}`);
        }
      });
    }

    appliedRef.current.clear();
  }, []);

  /**
   * Debounced translate triggered by DOM mutations
   */
  const debouncedTranslate = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(translatePage, DEBOUNCE_MS);
  }, [translatePage]);

  // Effect: handle language changes
  useEffect(() => {
    if (lastLangRef.current !== language) {
      // Language changed — restore originals first, then re-translate
      restoreOriginals();
      lastLangRef.current = language;
    }

    if (language === 'en') {
      // English — make sure everything is restored
      restoreOriginals();
      return;
    }

    // Initial translation
    const timer = setTimeout(translatePage, 150);

    return () => clearTimeout(timer);
  }, [language, translatePage, restoreOriginals]);

  // Effect: set up MutationObserver to catch dynamic content
  useEffect(() => {
    if (language === 'en') {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    observerRef.current = new MutationObserver((mutations) => {
      // Check if any mutation added new text-containing nodes
      let hasNewContent = false;
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          const addedNodes = Array.from(mutation.addedNodes);
          for (let i = 0; i < addedNodes.length; i++) {
            const node = addedNodes[i];
            if (node.nodeType === Node.TEXT_NODE && isTranslatable(node.textContent || '')) {
              hasNewContent = true;
              break;
            }
            if (node.nodeType === Node.ELEMENT_NODE) {
              const el = node as Element;
              if (!SKIP_TAGS.has(el.tagName) && !el.hasAttribute('data-translated')) {
                hasNewContent = true;
                break;
              }
            }
          }
        }
        if (mutation.type === 'characterData') {
          hasNewContent = true;
        }
        if (hasNewContent) break;
      }

      if (hasNewContent) {
        debouncedTranslate();
      }
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [language, debouncedTranslate]);

  // This component renders nothing — it's pure side-effects
  return null;
};

export default AutoTranslator;
